import React, {useEffect, useState} from 'react';
import {getRemotionEnvironment, useBufferState, useCurrentFrame, useDelayRender, useVideoConfig} from 'remotion';
import {BrandIntroPaint} from '../../../../src/remotion/templates/BrandIntroCard';
import {loadFontFromTextItem} from '../../vendor/src/features/remotion-editor/editor/utils/text/load-font-from-text-item';
import {useCroppableLayer} from '../../vendor/src/features/remotion-editor/editor/items/croppable-layer';
import {ZoomWrapper} from '../../vendor/src/features/remotion-editor/editor/items/zoom-wrapper';
import {calculateAnimationsStyle, mergeAnimationStyle} from '../../vendor/src/features/remotion-editor/editor/items/animation/calculate-animation';
import {calculateFadeInOpacity, calculateFadeOutOpacity} from '../../vendor/src/features/remotion-editor/editor/items/video/calculate-fade';
import {sourceBrandIntroOfItem, sourceBrandIntroPaintClock, type SourceBrandIntroItem} from './source-brand-intro';

/** Native runtime only. C must pin imports in shared source painter to this same React/Remotion. */
export function SourceBrandIntroCard({item, cropBackground = false, zIndex, shouldBuffer = true}: {
  item: SourceBrandIntroItem; cropBackground?: boolean; zIndex?: number; shouldBuffer?: boolean;
}) {
  const data = sourceBrandIntroOfItem(item);
  if (!data) throw Error('오프닝 카드 설정을 확인해 주세요.');
  const frame = useCurrentFrame(), {fps} = useVideoConfig(), delays = useDelayRender(), buffer = useBufferState();
  const [ready, setReady] = useState(false), [error, setError] = useState<Error | null>(null);
  useEffect(() => {
    let active = true, done = false;
    const handle = delays.delayRender('오프닝 카드 글꼴 준비');
    const playback = !getRemotionEnvironment().isRendering && shouldBuffer ? buffer.delayPlayback() : null;
    const release = () => { if (done) return; done = true; clearTimeout(timer); playback?.unblock(); delays.continueRender(handle); };
    const fail = (reason: unknown) => {
      if (active) {
        const e = reason instanceof Error ? reason : Error('오프닝 카드 글꼴을 불러오지 못했습니다. 다시 시도해 주세요.');
        setError(e); if (getRemotionEnvironment().isRendering) delays.cancelRender(e);
      }
      release();
    };
    const timer = setTimeout(() => fail(Error('오프닝 카드 글꼴 준비 시간이 초과됐습니다. 다시 시도해 주세요.')), 15000);
    void Promise.all(['800', '500'].map(fontWeight => loadFontFromTextItem({fontFamily: 'Noto Sans KR', fontWeight,
      fontVariant: 'normal', fontInfosDuringRendering: null}))).then(() => { if (active) setReady(true); release(); }).catch(fail);
    return () => { active = false; release(); };
  }, [shouldBuffer]);
  const opacity = item.opacity * calculateFadeInOpacity({fadeWindow: item.visualFadeWindow, currentFrame: frame,
    fadeInDurationInSeconds: item.fadeInDurationInSeconds, framesPerSecond: fps}) * calculateFadeOutOpacity({fadeWindow: item.visualFadeWindow,
    currentFrame: frame, fadeOutDurationInSeconds: item.fadeOutDurationInSeconds, framesPerSecond: fps, totalDurationInFrames: item.durationInFrames});
  const {innerStyle, outerStyle} = useCroppableLayer({item, rotation: item.rotation, opacity, borderRadius: item.borderRadius, cropBackground, zIndex});
  const animation = calculateAnimationsStyle({animations: item.animations ?? [], currentFrame: frame, framesPerSecond: fps,
    totalDurationInFrames: item.durationInFrames, positionKeyframes: item.positionKeyframes});
  const clock = sourceBrandIntroPaintClock(data, frame, fps);
  if (error) throw error;
  return <div style={mergeAnimationStyle({...outerStyle, backgroundColor: item.backgroundColor, boxShadow: item.boxShadow}, animation)}>
    <ZoomWrapper itemId={item.id} zoomKeyframes={cropBackground ? undefined : item.zoomKeyframes} frame={frame}>
      <div style={{...innerStyle, visibility: ready ? 'visible' : 'hidden'}}>
        <div style={{position: 'relative', width: data.width, height: data.height, transformOrigin: '0 0',
          transform: `scale(${item.width / data.width},${item.height / data.height})`}}>
          <BrandIntroPaint title={data.title} subtitle={data.subtitle} bg={data.bg} fg={data.fg} width={data.width} height={data.height} {...clock}/>
        </div>
      </div>
    </ZoomWrapper>
  </div>;
}
